import i18n from '@plugins/i18n';

export const isEmpty = (value: unknown): boolean => {
    if (value === null || value === undefined || value === '') {
        return true;
    }

    return !!(Array.isArray(value) && value.length === 0);
};

export const isNullOrUndefined = (value: unknown): value is undefined | null => {
    return value === null || value === undefined;
};

export const isEmptyArray = (arr: unknown): boolean => {
    return Array.isArray(arr) && arr.length === 0;
};

export const requiredValidator = (value: unknown) => {
    if (isNullOrUndefined(value) || isEmptyArray(value) || value === false) {
        return i18n.global.t('validation.messages.required');
    }

    return !!String(value).trim().length || i18n.global.t('validation.messages.required');
};

const emailRegex =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const isValidEmail = (value: string): boolean => {
    return emailRegex.test(value);
};

export const emailValidator = (value: unknown) => {
    if (isEmpty(value)) {
        return true;
    }

    if (Array.isArray(value)) {
        return value.every(val => emailRegex.test(String(val))) || i18n.global.t('validation.messages.email');
    }

    return emailRegex.test(String(value)) || i18n.global.t('validation.messages.email');
};

export const passwordValidator = (password: string) => {
    const regExp = /(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}/;

    const validPassword = regExp.test(password);

    return validPassword || i18n.global.t('validation.messages.password');
};

export const confirmedValidator = (value: string, target: string) =>
    value === target || i18n.global.t('validation.messages.passwordConfirmation');

export const betweenValidator = (value: unknown, min: number, max: number) => {
    const valueAsNumber = Number(value);

    return (
        (Number(min) <= valueAsNumber && Number(max) >= valueAsNumber) ||
        i18n.global.t('validation.messages.between', { min, max })
    );
};

export const integerValidator = (value: unknown) => {
    if (isEmpty(value)) {
        return true;
    }

    if (Array.isArray(value)) {
        return value.every(val => /^-?[0-9]+$/.test(String(val))) || i18n.global.t('validation.messages.integer');
    }

    return /^-?[0-9]+$/.test(String(value)) || i18n.global.t('validation.messages.integer');
};

export const numberValidator = (value: unknown) => {
    const regex = /^-?\d+([.,]\d+)?$/;

    if (isEmpty(value)) {
        return true;
    }
    if (Array.isArray(value)) {
        return value.every(val => regex.test(String(val))) || i18n.global.t('validation.messages.number');
    }

    return regex.test(String(value)) || i18n.global.t('validation.messages.number');
};

export const regexValidator = (value: unknown, regex: RegExp | string): string | boolean => {
    if (isEmpty(value)) {
        return true;
    }

    let regeX = regex;
    if (typeof regeX === 'string') {
        regeX = new RegExp(regeX);
    }

    if (Array.isArray(value)) {
        return value.every(val => regexValidator(val, regeX));
    }

    return regeX.test(String(value)) || i18n.global.t('validation.messages.regex');
};

export const alphaValidator = (value: unknown) => {
    if (isEmpty(value)) {
        return true;
    }

    return /^[A-Z]*$/i.test(String(value)) || i18n.global.t('validation.messages.alpha');
};

export const urlValidator = (value: unknown) => {
    if (isEmpty(value)) {
        return true;
    }

    const re = /^(http[s]?:\/\/){0,1}(www\.){0,1}[a-zA-Z0-9\.\-]+\.[a-zA-Z]{2,5}[\.]{0,1}/;

    return re.test(String(value)) || i18n.global.t('validation.messages.url');
};

export const lengthValidator = (value: number | null, length: number) => {
    if (value === null) {
        return true;
    }

    return value === length || i18n.global.t('validation.messages.minLength', { minLength: length });
};

export const minLengthValidator = (value: number | null, minLength: number) => {
    if (value === null) {
        return true;
    }

    return value >= minLength || i18n.global.t('validation.messages.minLength', { minLength });
};

export const maxLengthValidator = (value: number | null, maxLength: number) => {
    if (value === null) {
        return true;
    }

    return value <= maxLength || i18n.global.t('validation.messages.maxLength', { maxLength });
};

export const alphaDashValidator = (value: unknown) => {
    if (isEmpty(value)) {
        return true;
    }

    const valueAsString = String(value);

    return /^[0-9A-Z_-]*$/i.test(valueAsString) || i18n.global.t('validation.messages.alphaDash');
};

export const upperCaseValidator = (value: unknown) => {
    if (isEmpty(value)) {
        return true;
    }

    return /^[A-Z]*$/.test(String(value)) || i18n.global.t('validation.messages.upperCase');
};

export const lowerCaseValidator = (value: unknown) => {
    if (isEmpty(value)) {
        return true;
    }

    return /^[a-z]*$/.test(String(value)) || i18n.global.t('validation.messages.upperCase');
};

export const uniqueValidator = (value: unknown, existingValues: unknown[]) => {
    if (isEmpty(value)) {
        return true;
    }

    return !existingValues.includes(value) || i18n.global.t('validation.messages.unique');
};

export const minArrayLengthValidator = (minLength: number) => (value: unknown) => {
    if (!Array.isArray(value)) {
        return true;
    }

    return value.length >= minLength || i18n.global.t('validation.messages.minArrayLength', { minLength });
};

export const maxValidator = (max: number | string) => (value: number | null) => {
    if (value === null) {
        return true;
    }

    const parsedValue = parseFloat(String(value));
    const maxNumber = typeof max === 'string' ? parseFloat(max) : max;

    return (
        Number.isNaN(parsedValue) ||
        parsedValue <= maxNumber ||
        i18n.global.t('validation.messages.max', { max: maxNumber })
    );
};

export const minValidator = (min: number | string) => (value: number | null) => {
    if (value === null) {
        return true;
    }

    const parsedValue = parseFloat(String(value));
    const minNumber = typeof min === 'string' ? parseFloat(min) : min;

    return (
        Number.isNaN(parsedValue) ||
        parsedValue >= minNumber ||
        i18n.global.t('validation.messages.min', { min: minNumber })
    );
};
